import { useCallback, useEffect, useState } from "react";
import type { AxiosInstance } from "axios";
import { isRequestCanceled } from "@/services/apiErrors";
import { fetchProjects } from "@/services/projectsApi";
import type { Project } from "@/types/project";

interface UseProjectsOptions {
  enabled: boolean;
  apiOverride?: AxiosInstance;
}

interface UseProjectsResult {
  projects: Project[];
  loading: boolean;
  error: unknown;
  refetch: () => void;
}

export function useProjects({
  enabled,
  apiOverride,
}: UseProjectsOptions): UseProjectsResult {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const refetch = useCallback(() => {
    setReloadCounter((counter) => counter + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setProjects([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void (async () => {
      try {
        const items = await fetchProjects({}, apiOverride);
        if (cancelled) return;
        setProjects(items);
        setError(null);
      } catch (caught) {
        if (cancelled || isRequestCanceled(caught)) return;
        setError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, reloadCounter, apiOverride]);

  return { projects, loading: isLoading, error, refetch };
}